import React from "react";
import { motion } from "framer-motion";
import { Mail, Search, CheckCircle2, XCircle } from "lucide-react";
import { Drawer } from "./Drawer";
import { useDashTheme } from "../hooks/useDashTheme";
import { Avatar, Badge } from "./primitives";
import { EmptyState } from "./DataTable";

// V2-native Gmail connections — member list with connected status, search +
// filter. Data passed in from the dashboard hook (org-gmail-status).
// 2026-08-30: now a side drawer (shared `Drawer` shell) like every other
// DashboardV2 surface; the export name is kept so callers are untouched.
export function GmailModal({ open, onClose, gmail }) {
  const t = useDashTheme();
  const [q, setQ] = React.useState("");
  const [filter, setFilter] = React.useState("all"); // all | connected | not_connected

  React.useEffect(() => { if (open) { setQ(""); setFilter("all"); } }, [open]);

  const connected = gmail?.connectedCount ?? 0;
  const total = gmail?.total ?? (gmail?.members?.length || 0);
  const notConnected = gmail?.notConnectedCount ?? Math.max(0, total - connected);

  const members = React.useMemo(() => {
    let list = gmail?.members || [];
    if (filter === "connected") list = list.filter((m) => m.gmailConnected);
    if (filter === "not_connected") list = list.filter((m) => !m.gmailConnected);
    if (q) {
      const s = q.toLowerCase();
      list = list.filter((m) =>
        `${m.firstName || ""} ${m.lastName || ""}`.toLowerCase().includes(s) ||
        (m.email || "").toLowerCase().includes(s));
    }
    return list;
  }, [gmail, filter, q]);

  const filters = [
    { key: "all", label: `All (${total})` },
    { key: "connected", label: `Connected (${connected})` },
    { key: "not_connected", label: `Not connected (${notConnected})` },
  ];

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Gmail connections"
      subtitle={`${connected} of ${total} members connected`}
      icon={<Mail size={18} />}
      width={520}
      toolbar={(
        <div style={{ display: "flex", flexDirection: "column", gap: t.s[3] }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, height: 36, padding: "0 12px", border: `1px solid ${t.c.border}`, borderRadius: t.r.sm, background: t.c.surfaceMuted }}>
            <Search size={14} style={{ color: t.c.textTertiary }} />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search members..."
              style={{ flex: 1, border: "none", outline: "none", background: "transparent", color: t.c.text, fontSize: t.font.size.md, fontFamily: t.font.family }} />
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {filters.map((f) => {
              const active = filter === f.key;
              return (
                <button key={f.key} onClick={() => setFilter(f.key)}
                  style={{
                    height: 28, padding: "0 11px", borderRadius: 99, cursor: "pointer",
                    border: `1px solid ${active ? "transparent" : t.c.border}`,
                    background: active ? t.c.primary : "transparent", color: active ? "#fff" : t.c.textSecondary,
                    fontSize: t.font.size.sm, fontWeight: 500, fontFamily: t.font.family,
                  }}>{f.label}</button>
              );
            })}
          </div>
        </div>
      )}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: t.s[2] }}>
        {members.length === 0 ? (
          <EmptyState icon={<Mail size={22} />} title="No members" description={q ? "No members match your search." : "No members found."} />
        ) : (
          members.map((m, i) => {
            const name = `${m.firstName || ""} ${m.lastName || ""}`.trim() || m.email || "Member";
            return (
              <motion.div key={m._id || m.userId || i}
                initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2, delay: Math.min(i * 0.02, 0.18) }}
                style={{ display: "flex", alignItems: "center", gap: 10, padding: `${t.s[2]}px ${t.s[3]}px`, borderRadius: t.r.md, border: `1px solid ${t.c.border}` }}
              >
                <Avatar name={name} size={32} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: t.font.size.md, fontWeight: 500, color: t.c.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name}</div>
                  {m.email && <div style={{ fontSize: t.font.size.xs, color: t.c.textTertiary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.email}</div>}
                </div>
                {m.gmailConnected ? (
                  <Badge tone="positive"><CheckCircle2 size={12} /> Connected</Badge>
                ) : (
                  <Badge tone="neutral"><XCircle size={12} /> Not connected</Badge>
                )}
              </motion.div>
            );
          })
        )}
      </div>
    </Drawer>
  );
}
